import { Seo, Reveal } from "../components/Seo";
import { PageHero } from "../components/blocks";
import { CONTACT_INFO } from "../lib/site";

const DRAFT_NOTE =
  "This is a reviewable draft prepared for Softline Nigeria's content owners. It is not legal advice. Passages marked with square brackets are placeholders that must be replaced with verified information before this page is treated as final.";

function Section({ n, title, children }) {
  return (
    <Reveal delay={0.03}>
      <section className="border-t border-charcoal/10 py-8" data-testid={`legal-section-${n}`}>
        <h2 className="font-display font-bold text-xl md:text-2xl">{title}</h2>
        <div className="mt-4 space-y-4 text-[15px] leading-relaxed text-charcoal/75">{children}</div>
      </section>
    </Reveal>
  );
}

const CONTENT = {
  privacy: {
    title: "Privacy Policy",
    seo: "Privacy Policy — Softline Nigeria",
    desc: "How Softline Technologies and Services Nigeria Limited collects, uses and protects personal information, prepared under the Nigeria Data Protection Act 2023.",
    path: "/privacy-policy",
    sections: [
      {
        t: "1. Who we are",
        c: (
          <>
            <p>
              Softline Technologies and Services Nigeria Limited ("Softline Nigeria", "we") is a Nigerian technology company
              providing digital products and enterprise technology services. Registration number: {CONTACT_INFO.regNo}.
              Offices: {CONTACT_INFO.offices[0]}; {CONTACT_INFO.offices[1]}. Contact for privacy matters: {CONTACT_INFO.email}.
            </p>
            <p>
              For the purposes of applicable data protection law, Softline Nigeria is the data controller for personal
              information collected through this website.
            </p>
          </>
        ),
      },
      {
        t: "2. Information we collect",
        c: (
          <>
            <p>Through this website we collect only what we need to respond to you:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Contact form: full name, business email, organization, enquiry category, message, and optionally telephone, industry and country.</li>
              <li>Product preview form: product of interest, organization, business contact, intended use case, optional preferred meeting date.</li>
              <li>Technical data: standard server logs (IP address, timestamps) retained for security and abuse prevention.</li>
            </ul>
            <p>We do not intentionally collect sensitive personal data through this website, and we ask you not to submit it.</p>
          </>
        ),
      },
      {
        t: "3. Why we process it (legal bases)",
        c: (
          <>
            <ul className="list-disc pl-5 space-y-2">
              <li>To respond to your enquiry or arrange a requested session — necessary for our legitimate interests, or performance of a contract where engagement follows.</li>
              <li>To keep this website secure and prevent abuse — legitimate interests.</li>
              <li>Where consent is required (for example, optional updates, if introduced), we ask for it explicitly and you may withdraw it.</li>
            </ul>
          </>
        ),
      },
      {
        t: "4. Sharing and transfers",
        c: (
          <p>
            Enquiries are routed internally to the responsible Softline Nigeria team. We do not sell personal information.
            Where processors (for example hosting or email providers) are used, data processing agreements are put in place.
            Cross-border processing, if applicable, is assessed against the Nigeria Data Protection Act 2023 and applicable
            NDPC guidance: <span className="font-semibold">[cross-border transfer details to be completed by the content owners]</span>.
          </p>
        ),
      },
      {
        t: "5. Retention",
        c: (
          <p>
            Enquiry records are retained only as long as needed for the purposes above and applicable statutory periods:
            <span className="font-semibold"> [retention periods to be confirmed and inserted]</span>. Server security logs are retained for a short defined window.
          </p>
        ),
      },
      {
        t: "6. Your rights",
        c: (
          <p>
            Under the Nigeria Data Protection Act 2023 you may request access to, correction of, or deletion of your personal
            information, and may object to or restrict certain processing. To exercise these rights, contact{" "}
            {CONTACT_INFO.email}. You may also lodge a complaint with the Nigeria Data Protection Commission.
          </p>
        ),
      },
      {
        t: "7. Security",
        c: (
          <p>
            We apply appropriate technical and organisational measures to the website and its forms, including transport
            encryption, server-side validation, rate limiting and access controls. No public website can guarantee absolute
            security; we do not make such a claim.
          </p>
        ),
      },
      {
        t: "8. Changes to this notice",
        c: <p>This draft notice will be versioned and dated upon approval. Material changes will be highlighted on this page.</p>,
      },
    ],
  },
  cookies: {
    title: "Cookie Notice",
    seo: "Cookie Notice — Softline Nigeria",
    desc: "How cookies and similar technologies are used on the Softline Nigeria corporate website.",
    path: "/cookie-notice",
    sections: [
      {
        t: "1. What we use today",
        c: (
          <p>
            This website is a corporate information site. At launch it sets no advertising or profiling cookies. Any strictly
            necessary or functional storage is limited to what the site needs to operate and remain secure.
          </p>
        ),
      },
      {
        t: "2. Analytics and measurement",
        c: (
          <p>
            Privacy-respecting measurement may be used to understand aggregate site usage:{" "}
            <span className="font-semibold">[analytics tools and their cookie/consent approach to be confirmed before production]</span>.
            Where consent is required for non-essential measurement, a consent control will be provided on this page.
          </p>
        ),
      },
      {
        t: "3. Third-party content",
        c: (
          <p>
            Embedded third-party content, if introduced (for example video or social embeds), may set its own cookies. This
            notice will be updated before any such content is enabled.
          </p>
        ),
      },
      {
        t: "4. Managing cookies",
        c: (
          <p>
            You can control or delete cookies through your browser settings at any time. Restricting strictly necessary
            storage may affect site functionality.
          </p>
        ),
      },
    ],
  },
  terms: {
    title: "Terms of Use",
    seo: "Terms of Use — Softline Nigeria",
    desc: "Terms governing use of the Softline Nigeria corporate website, prepared as a reviewable draft.",
    path: "/terms",
    sections: [
      {
        t: "1. Acceptance",
        c: (
          <p>
            By using this website you agree to these Terms of Use. If you do not agree, please do not use the site. These
            terms apply to the Nigerian website only; the Swedish parent site publishes its own terms.
          </p>
        ),
      },
      {
        t: "2. Ownership and content",
        c: (
          <p>
            The Softline name, logo and site content are owned by their respective Softline group entities. Product names —
            SoftEbox, myID, CoFundApp — are Softline product marks. Content is provided for information; it may change
            without notice and does not constitute a contractual offer.
          </p>
        ),
      },
      {
        t: "3. Acceptable use",
        c: (
          <>
            <p>You agree not to:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>interfere with or attempt to gain unauthorised access to the site, its APIs or related systems;</li>
              <li>submit unlawful, misleading or unwanted commercial content through our forms;</li>
              <li>copy site content or brand assets without permission.</li>
            </ul>
          </>
        ),
      },
      {
        t: "4. Product information and availability",
        c: (
          <p>
            Product pages display a readiness status approved by Softline Nigeria. Capabilities marked as roadmap are not
            yet available. Nothing on this website constitutes a warranty, certification or regulatory endorsement.
          </p>
        ),
      },
      {
        t: "5. Liability",
        c: (
          <p>
            To the extent permitted by law, Softline Nigeria is not liable for indirect or consequential loss arising from
            use of this website. Nothing in these terms limits liability that cannot lawfully be limited:
            <span className="font-semibold"> [final liability wording to be confirmed by legal counsel]</span>.
          </p>
        ),
      },
      {
        t: "6. Governing law",
        c: (
          <p>
            These terms are governed by the laws of the Federal Republic of Nigeria:{" "}
            <span className="font-semibold">[venue and dispute-resolution details to be confirmed]</span>.
          </p>
        ),
      },
    ],
  },
};

export default function Legal({ kind }) {
  const c = CONTENT[kind];
  return (
    <>
      <Seo title={`${c.title} — Softline Nigeria`} description={c.desc} path={c.path} />
      <PageHero
        eyebrow="Legal"
        title={[c.title]}
        lead="A reviewable draft prepared for Softline Nigeria's content owners."
        crumbs={[{ label: "Home", to: "/" }, { label: c.title }]}
      />
      <section className="bg-white" data-testid="legal-content">
        <div className="shell py-16 md:py-24">
          <Reveal>
            <div className="border border-lime bg-lime/10 p-5 text-sm text-charcoal/80 leading-relaxed" data-testid="legal-draft-banner">
              <span className="font-semibold">Draft for review.</span> {DRAFT_NOTE}
            </div>
          </Reveal>
          <div className="max-w-3xl mt-4">
            {c.sections.map((s, i) => (
              <Section key={i} n={i} title={s.t}>
                {s.c}
              </Section>
            ))}
            <p className="border-t border-charcoal/10 pt-6 text-xs text-charcoal/50">
              Last updated: 18 October 2026 (draft). Placeholders appear in square brackets.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
